export const organizationTypeIds = [
    "islamic_center",
    "mosque",
    "weekend_school",
    "islamic_school",
    "charity",
    "relief_organization",
    "youth_organization",
    "food_pantry",
    "other_nonprofit",
];
export const organizationLifecycleStatuses = [
    "provisioning",
    "trial",
    "active",
    "past_due",
    "restricted",
    "suspended",
    "archived",
];
export const organizationCampusStatuses = ["planned", "active", "inactive", "archived"];
export const organizationLocaleCodes = ["en", "ar"];
export function isOrganizationTypeId(value) {
    return organizationTypeIds.includes(value);
}
export function isOrganizationLifecycleStatus(value) {
    return organizationLifecycleStatuses.includes(value);
}
export function isOrganizationCampusStatus(value) {
    return organizationCampusStatuses.includes(value);
}
export function isOrganizationLocaleCode(value) {
    return organizationLocaleCodes.includes(value);
}
export function isOpaqueProductionOrganizationId(value) {
    return /^org_[0-9a-z]{12,32}$/.test(value);
}
export function isSyntheticSeedOrganizationId(value) {
    return /^org-[a-z0-9][a-z0-9-]{1,62}$/.test(value);
}
export function isFirestoreSafeOrganizationId(value) {
    return (value.length <= 80 &&
        !value.includes("/") &&
        (isOpaqueProductionOrganizationId(value) || isSyntheticSeedOrganizationId(value)));
}
export function isFirestoreSafeCampusId(value) {
    return /^campus-[a-z0-9][a-z0-9-]{1,62}$/.test(value) && !value.includes("/");
}
export function isValidHexColor(value) {
    return /^#[0-9a-fA-F]{6}$/.test(value);
}
export function isOrganizationBrandingStoragePath(organizationId, value) {
    if (value === null) {
        return true;
    }
    return (value.length <= 512 &&
        value.startsWith(`organizations/${organizationId}/branding/`) &&
        !value.includes("..") &&
        !value.endsWith("/"));
}
export function buildOrganizationDirectoryProfile(organization, copy) {
    return {
        address: organization.address,
        brandColor: organization.brandColor,
        branding: organization.branding,
        category: copy.category,
        city: organization.city,
        connectionPolicy: copy.connectionPolicy,
        country: organization.country,
        currency: organization.currency,
        defaultLocale: organization.defaultLocale,
        displayName: organization.displayName,
        id: organization.organizationId,
        legalName: organization.legalName,
        openConnection: copy.connectionPolicy === "open",
        organizationId: organization.organizationId,
        organizationType: organization.organizationType,
        publicContact: organization.publicContact,
        publicVisibility: organization.publicVisibility,
        supportedLocales: organization.supportedLocales,
        timeZone: organization.timeZone,
        welcomeHeadline: copy.welcomeHeadline,
        welcomeHeadlineAr: copy.welcomeHeadlineAr,
        welcomeSummary: copy.welcomeSummary,
        welcomeSummaryAr: copy.welcomeSummaryAr,
    };
}
export function validateCanonicalOrganization(organization) {
    const errors = [];
    if (!isRecord(organization)) {
        return { valid: false, errors: ["Organization must be an object."] };
    }
    const organizationId = stringValue(organization.organizationId);
    if (!isFirestoreSafeOrganizationId(organizationId)) {
        errors.push("Organization ID must be Firestore-safe and immutable.");
    }
    if (stringValue(organization.id) !== organizationId) {
        errors.push("Organization id alias must match organizationId.");
    }
    validateRequiredText("Display name", organization.displayName, 120, errors);
    const organizationType = stringValue(organization.organizationType);
    if (!isOrganizationTypeId(organizationType)) {
        errors.push("Organization type must be a stable supported identifier.");
    }
    const status = stringValue(organization.status);
    if (!isOrganizationLifecycleStatus(status)) {
        errors.push("Organization status must be a supported lifecycle status.");
    }
    const primaryCampusId = stringValue(organization.primaryCampusId);
    if (!isFirestoreSafeCampusId(primaryCampusId)) {
        errors.push("Primary campus ID must be a Firestore-safe campus ID.");
    }
    validateLocalization(organization, errors);
    validateAddress(organization.address, errors);
    validatePublicContact(organization.publicContact, errors);
    validatePublicVisibility(organization.publicVisibility, errors);
    validateBranding(organizationId, organization.branding, errors);
    validateConfigPlaceholders(organization.moduleReadiness, organization.configVersions, errors);
    const forbiddenKeys = ["connections", "members", "membership", "permissions", "roles"];
    for (const key of forbiddenKeys) {
        if (key in organization) {
            errors.push(`Organization must not carry ${key} data on the root document.`);
        }
    }
    if (!isRecord(organization.source)) {
        errors.push("Organization source metadata is required.");
    }
    if (!isRecord(organization.migration)) {
        errors.push("Organization migration metadata is required.");
    }
    return { valid: errors.length === 0, errors };
}
export function validateOrganizationDirectoryProfile(profile) {
    const errors = [];
    if (!isRecord(profile)) {
        return { valid: false, errors: ["Organization directory profile must be an object."] };
    }
    const organizationId = stringValue(profile.organizationId);
    if (!isFirestoreSafeOrganizationId(organizationId)) {
        errors.push("Public profile organization ID must be Firestore-safe.");
    }
    if (stringValue(profile.id) !== organizationId) {
        errors.push("Public profile id alias must match organizationId.");
    }
    validateRequiredText("Public display name", profile.displayName, 120, errors);
    const organizationType = stringValue(profile.organizationType);
    if (!isOrganizationTypeId(organizationType)) {
        errors.push("Public profile must expose a stable organization type.");
    }
    if (!isPublicCategory(stringValue(profile.category))) {
        errors.push("Public profile category must be an accepted display category.");
    }
    validateLocalization(profile, errors);
    validateAddress(profile.address, errors);
    validatePublicContact(profile.publicContact, errors);
    validatePublicVisibility(profile.publicVisibility, errors);
    validateBranding(organizationId, profile.branding, errors);
    validateRequiredText("Welcome headline", profile.welcomeHeadline, 180, errors);
    validateOptionalArabicText("Arabic welcome headline", profile.welcomeHeadlineAr, 180, errors);
    validateRequiredText("Welcome summary", profile.welcomeSummary, 600, errors);
    validateOptionalArabicText("Arabic welcome summary", profile.welcomeSummaryAr, 600, errors);
    if (!isProfileConnectionPolicy(stringValue(profile.connectionPolicy))) {
        errors.push("Public profile connection policy must be open or approval_required.");
    }
    if (profile.openConnection !== (profile.connectionPolicy === "open")) {
        errors.push("Public profile openConnection must match connectionPolicy.");
    }
    const forbiddenKeys = ["config", "moduleReadiness", "private", "roles"];
    for (const key of forbiddenKeys) {
        if (key in profile) {
            errors.push(`Public profile must not expose ${key}.`);
        }
    }
    return { valid: errors.length === 0, errors };
}
export function validateOrganizationCampus(campus) {
    const errors = [];
    if (!isRecord(campus)) {
        return { valid: false, errors: ["Organization campus must be an object."] };
    }
    const organizationId = stringValue(campus.organizationId);
    const campusId = stringValue(campus.campusId);
    if (!isFirestoreSafeOrganizationId(organizationId)) {
        errors.push("Campus organization ID must be Firestore-safe.");
    }
    if (!isFirestoreSafeCampusId(campusId)) {
        errors.push("Campus ID must be Firestore-safe.");
    }
    if (stringValue(campus.id) !== campusId) {
        errors.push("Campus id alias must match campusId.");
    }
    validateRequiredText("Campus name", campus.name, 120, errors);
    const status = stringValue(campus.status);
    if (!isOrganizationCampusStatus(status)) {
        errors.push("Campus status must be supported.");
    }
    validateAddress(campus.address, errors);
    validatePublicContact(campus.publicContact, errors);
    if (!isRecord(campus.geo)) {
        errors.push("Campus geo placeholder is required.");
    }
    else {
        validateNullableCoordinate("Campus latitude", campus.geo.latitude, -90, 90, errors);
        validateNullableCoordinate("Campus longitude", campus.geo.longitude, -180, 180, errors);
    }
    const timeZoneOverride = campus.timeZoneOverride;
    if (timeZoneOverride !== null &&
        !(typeof timeZoneOverride === "string" && isSupportedTimeZone(timeZoneOverride))) {
        errors.push("Campus timezone override must be null or a valid timezone.");
    }
    if (!isRecord(campus.operatingHours) || campus.operatingHours.status !== "not_configured") {
        errors.push("Campus operating hours must remain a placeholder.");
    }
    if (!isRecord(campus.accessibility) || campus.accessibility.status !== "not_configured") {
        errors.push("Campus accessibility must remain a placeholder.");
    }
    if ("prayer" in campus || "spaces" in campus || "events" in campus) {
        errors.push("Campus must not embed future module records.");
    }
    return { valid: errors.length === 0, errors };
}
export function validatePrimaryCampusConsistency(organization, campuses) {
    const errors = [];
    const primaryCampus = campuses.find((campus) => campus.campusId === organization.primaryCampusId);
    if (!primaryCampus) {
        errors.push("Organization primaryCampusId must reference a seeded campus.");
    }
    else if (primaryCampus.organizationId !== organization.organizationId) {
        errors.push("Primary campus must belong to the same organization.");
    }
    for (const campus of campuses) {
        if (campus.organizationId !== organization.organizationId) {
            errors.push("Campus list must only contain campuses for the organization.");
        }
    }
    return { valid: errors.length === 0, errors };
}
function validateLocalization(record, errors) {
    const defaultLocale = stringValue(record.defaultLocale);
    const defaultLocaleIsValid = isOrganizationLocaleCode(defaultLocale);
    if (!defaultLocaleIsValid) {
        errors.push("Default locale must be en or ar.");
    }
    const supportedLocales = Array.isArray(record.supportedLocales) ? record.supportedLocales : [];
    const supportedLocalesAreValid = supportedLocales.every((locale) => typeof locale === "string" && isOrganizationLocaleCode(locale));
    if (supportedLocales.length === 0 ||
        !supportedLocalesAreValid ||
        !defaultLocaleIsValid ||
        !supportedLocales.includes(defaultLocale)) {
        errors.push("Supported locales must include the default locale and only en/ar.");
    }
    if (!isSupportedTimeZone(stringValue(record.timeZone))) {
        errors.push("Organization timezone must be valid.");
    }
    if (!/^[A-Z]{3}$/.test(stringValue(record.currency))) {
        errors.push("Organization currency must be an uppercase ISO 4217 code.");
    }
    if (!/^[A-Z]{2}$/.test(stringValue(record.country))) {
        errors.push("Organization country must be an uppercase ISO 3166-1 alpha-2 code.");
    }
}
function validateAddress(address, errors) {
    if (!isRecord(address)) {
        errors.push("Address summary is required.");
        return;
    }
    validateRequiredText("Address city", address.city, 120, errors);
    validateRequiredText("Address region", address.region, 80, errors);
    validateRequiredText("Address postal code", address.postalCode, 24, errors);
    validateRequiredText("Address summary", address.summary, 180, errors);
    if (!/^[A-Z]{2}$/.test(stringValue(address.country))) {
        errors.push("Address country must be uppercase ISO 3166-1 alpha-2.");
    }
}
function validatePublicContact(contact, errors) {
    if (!isRecord(contact)) {
        errors.push("Public contact placeholder is required.");
        return;
    }
    if (!isNullableEmail(contact.email)) {
        errors.push("Public email must be null or a valid email-shaped value.");
    }
    if (!isNullablePhone(contact.phone)) {
        errors.push("Public phone must be null or a short phone value.");
    }
    if (!isNullableHttpsUrl(contact.websiteUrl)) {
        errors.push("Website URL must be null or https.");
    }
    if (!Array.isArray(contact.socialLinks) ||
        !contact.socialLinks.every((link) => isRecord(link) && isNullableHttpsUrl(link.url))) {
        errors.push("Social links must be an array of safe https links.");
    }
}
function validatePublicVisibility(visibility, errors) {
    if (!isRecord(visibility) ||
        typeof visibility.directory !== "boolean" ||
        typeof visibility.discovery !== "boolean" ||
        typeof visibility.profile !== "boolean") {
        errors.push("Public visibility flags are required.");
    }
}
function validateBranding(organizationId, branding, errors) {
    if (!isRecord(branding)) {
        errors.push("Branding is required.");
        return;
    }
    validateRequiredText("Branding display name", branding.displayName, 120, errors);
    for (const key of ["primaryColor", "secondaryColor", "accentColor"]) {
        if (!isValidHexColor(stringValue(branding[key]))) {
            errors.push(`${key} must be a 6-digit hex color.`);
        }
    }
    if (!isNullableStoragePath(branding.logoStoragePath)) {
        errors.push("Logo storage path must be null or a string.");
    }
    else if (!isOrganizationBrandingStoragePath(organizationId, branding.logoStoragePath)) {
        errors.push("Logo storage path must be under the organization branding path.");
    }
    if (!isNullableStoragePath(branding.coverImageStoragePath)) {
        errors.push("Cover image storage path must be null or a string.");
    }
    else if (!isOrganizationBrandingStoragePath(organizationId, branding.coverImageStoragePath)) {
        errors.push("Cover image storage path must be under the organization branding path.");
    }
    if (typeof branding.version !== "number" || branding.version < 1) {
        errors.push("Branding version must be a positive number.");
    }
}
function validateConfigPlaceholders(moduleReadiness, configVersions, errors) {
    const moduleKeys = [
        "announcements",
        "commerce",
        "events",
        "prayer",
        "ramadan",
        "spaces",
        "volunteers",
    ];
    if (!isRecord(moduleReadiness) ||
        !moduleKeys.every((key) => moduleReadiness[key] === "placeholder")) {
        errors.push("Module readiness must contain only placeholders.");
    }
    if (!isRecord(configVersions) ||
        typeof configVersions.branding !== "number" ||
        typeof configVersions.private !== "number" ||
        typeof configVersions.public !== "number") {
        errors.push("Config versions are required.");
    }
}
function validateNullableCoordinate(label, value, min, max, errors) {
    if (value === null) {
        return;
    }
    if (typeof value !== "number" || value < min || value > max) {
        errors.push(`${label} must be null or within range.`);
    }
}
function validateOptionalArabicText(label, value, maxLength, errors) {
    if (typeof value !== "string" || value.trim().length > maxLength)
        errors.push(`${label} must be ${maxLength} characters or fewer.`);
}
function validateRequiredText(label, value, maxLength, errors) {
    if (typeof value !== "string" || value.trim().length === 0 || value.trim().length > maxLength) {
        errors.push(`${label} is required.`);
    }
}
function isSupportedTimeZone(value) {
    if (value.trim().length === 0) {
        return false;
    }
    try {
        new Intl.DateTimeFormat("en-US", { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
function isNullableEmail(value) {
    return (value === null ||
        (typeof value === "string" && value.length <= 254 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)));
}
function isNullablePhone(value) {
    return value === null || (typeof value === "string" && value.length > 0 && value.length <= 32);
}
function isNullableHttpsUrl(value) {
    if (value === null) {
        return true;
    }
    if (typeof value !== "string" || value.length > 2048) {
        return false;
    }
    try {
        return new URL(value).protocol === "https:";
    }
    catch {
        return false;
    }
}
function isNullableStoragePath(value) {
    return value === null || typeof value === "string";
}
function stringValue(value) {
    return typeof value === "string" ? value.trim() : "";
}
function isPublicCategory(value) {
    return value === "Islamic center" || value === "Community center";
}
function isProfileConnectionPolicy(value) {
    return value === "open" || value === "approval_required";
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
